import React from 'react';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';

interface NutritionBlogProps {
  blog: {
    id: number;
    title: string;
    excerpt: string;
    image: string;
    readTime: string;
  };
}

export function NutritionBlog({ blog }: NutritionBlogProps) {
  return (
    <div className="theme-card group relative overflow-hidden h-full backdrop-blur-xl border-primary/20">
      {/* Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 group-hover:opacity-20 transition-opacity duration-500" />
      
      {/* Image Section with Overlay */}
      <div className="relative h-48 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 z-10 mix-blend-overlay group-hover:opacity-60 transition-opacity duration-500" />
        <img
          src={blog.image}
          alt={blog.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Glowing Orb */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      
      {/* Content Section */}
      <div className="relative z-20 p-4 bg-gradient-to-t from-background/95 to-background/80 backdrop-blur-sm">
        <div className="space-y-3">
          {/* Title with Hover Effect */}
          <h3 className="font-medium text-base line-clamp-2 group-hover:text-primary transition-colors duration-300">
            {blog.title}
          </h3>
          
          {/* Excerpt with Gradient Text */}
          <p className="text-xs text-muted-foreground line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-purple-500 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
            {blog.excerpt}
          </p>
          
          {/* Footer with Glowing Effects */}
          <div className="flex items-center justify-between pt-2 border-t border-muted/20">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="relative">
                <Clock className="w-3 h-3" />
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <span>{blog.readTime}</span>
            </div>
            
            {/* Read More Button with Glow */}
            <button className="relative flex items-center gap-1 text-primary text-xs font-medium group/btn">
              <span className="group-hover/btn:mr-1 transition-all duration-300">Read</span>
              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Hover Border Effect */}
      <div className="absolute inset-0 border border-primary/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Corner Accents */}
      <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-primary/20 rounded-tl-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-primary/20 rounded-br-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  );
}